import { Link, useNavigate } from 'react-router-dom';
import { Menu, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DarkModeToggle } from './DarkModeToggle';
import { useAuth } from '@/context/authContext';
import { displayEmail, useMyAccount } from '@/features/account/hooks/useMyAccount';

interface AppNavbarProps {
  onMenuClick?: () => void;
}

export function AppNavbar({ onMenuClick }: AppNavbarProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { data: account } = useMyAccount();

  const signOutUser = async () => {
    await logout();
    navigate("/login");
  };

  // Never the Auth address: accounts without a real mailbox have an internal one
  const getUserDisplayName = () => account?.full_name || 'Usuario';
  const subtitle = displayEmail(account) ?? account?.username ?? '';

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-card shadow-sm backdrop-blur-sm">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left side - Mobile menu + Logo */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMenuClick}
          >
            <Menu className="size-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>
          
          <Link to="/" className="group flex items-center gap-3 transition-opacity hover:opacity-80">
            <div className="relative h-12 w-auto transition-transform duration-300 group-hover:scale-105">
              <img 
                src="/addbolivialogo.png" 
                alt="Las Asambleas de Dios de Bolivia" 
                className="h-12 w-auto object-contain mix-blend-normal dark:mix-blend-lighten dark:brightness-100 dark:contrast-125"
                style={{
                  filter: 'drop-shadow(0 0 7px #ffffff)'
                }}
              />
            </div>
            <div className="hidden flex-col transition-all duration-300 group-hover:translate-x-1 sm:flex">
              <span className="text-lg font-bold leading-tight text-foreground">
                Las Asambleas de Dios de Bolivia
              </span>
              <span className="text-sm leading-tight text-muted-foreground">
                Portal de Administración
              </span>
            </div>
          </Link>
        </div>

        {/* Right side - Dark mode toggle + User menu */}
        <div className="flex items-center gap-2">
          <DarkModeToggle />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative size-10 rounded-full">
                <Avatar>
                  <AvatarFallback>
                    <User className="size-5" />
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {subtitle}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link to="/cuenta">
                    <User className="mr-2 size-4" />
                    <span>Mi cuenta</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={signOutUser}>
                  <LogOut className="mr-2 size-4" />
                  <span>Cerrar Sesión</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
